import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Lock } from "lucide-react";
import { useLanguage } from "../../App";

const AchievementBadge = ({ achievement, size = "md", onClick = null }) => {
  const { language } = useLanguage();
  const isEnglish = language === "en";

  if (!achievement) return null;

  const { icon, unlocked, tier, name, name_id, description, description_id } = achievement;

  const sizeClasses = {
    sm: "w-12 h-12 text-2xl",
    md: "w-16 h-16 text-3xl",
    lg: "w-20 h-20 text-4xl"
  };

  const tierColors = {
    bronze: "from-orange-400 to-orange-600",
    silver: "from-gray-300 to-gray-500",
    gold: "from-yellow-400 to-yellow-600",
    platinum: "from-cyan-300 to-blue-500"
  };

  const tierBorderColors = {
    bronze: "border-orange-400",
    silver: "border-gray-400",
    gold: "border-yellow-400",
    platinum: "border-cyan-400"
  };

  const BadgeContent = () => (
    <Card
      className={`
        cursor-pointer transition-all hover:shadow-lg
        ${unlocked ? "opacity-100" : "opacity-50"}
        ${onClick ? "hover:scale-105" : ""}
      `}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex flex-col items-center gap-2">
          {/* Icon */}
          <div
            className={`
              ${sizeClasses[size]} rounded-full flex items-center justify-center
              border-4 transition-all
              ${unlocked
                ? `bg-gradient-to-br ${tierColors[tier]} ${tierBorderColors[tier]} shadow-lg`
                : "bg-gray-200 border-gray-300"
              }
            `}
          >
            {unlocked ? (
              <span className="drop-shadow-md">{icon}</span>
            ) : (
              <Lock className="w-4 h-4 text-gray-400" />
            )}
          </div>

          {/* Title */}
          <div className="text-center">
            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">
              {isEnglish ? name : name_id}
            </p>
            {/* Tier badge */}
            {unlocked && (
              <Badge
                variant="secondary"
                className="mt-1 text-xs capitalize font-medium"
                style={{
                  backgroundColor: tierColors[tier]?.split(" ")[1]?.replace("to-", "").replace(/-\d+/, "-600") || "#6B7280",
                  color: "#1F2937"
                }}
              >
                {tier}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (unlocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <BadgeContent />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <p className="font-semibold">{isEnglish ? name : name_id}</p>
              <p className="text-xs text-muted-foreground">
                {isEnglish ? description : description_id}
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <BadgeContent />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-semibold text-muted-foreground">???</p>
            <p className="text-xs text-muted-foreground">
              {isEnglish ? "Complete the requirement to unlock" : "Selesaikan requirement untuk membuka"}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AchievementBadge;
